import React, { useEffect, useState, useRef } from 'react';
import './PropertySelection.scss';
import HTLogo from '../assets/ht_orange_logo_name.svg';
import LeftBg from '../assets/leftside_bg_grey.svg';
import RightBg from '../assets/rightside_bg_grey.svg';
import { Formik, Form } from 'formik';
import Modal from '../shared/molecules/modal/Modal';
import { PropertyCheckList } from 'shared';
import CheckTick from '../assets/check_tick.svg';
import Loader from '../shared/atoms/loader/Loader';
import {
  useGetPropertiesQuery,
  useSubmitSelectedPropertiesMutation,
  useGetRatePlansQuery,
  type PropertyFilterRequest,
  type Property as PropertyType,
  type RatePlanDto,
} from './apaleoSlice';

const PropertySelection: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [propertiesWithRatePlans, setPropertiesWithRatePlans] = useState<
    PropertyType[]
  >([]);
  const [ratePlansFetched, setRatePlansFetched] = useState<
    Record<string, boolean>
  >({});

  const {
    data: propertiesResponse,
    isLoading: isLoadingProperties,
    isError: isPropertiesError,
  } = useGetPropertiesQuery();

  // eslint-disable-next-line
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  const [currentPropertyIdForRatePlans, setCurrentPropertyIdForRatePlans] =
    useState<string | null>(null);
  // Track which property ID we're actually processing to avoid stale data
  const processingPropertyIdRef = useRef<string | null>(null);
  // eslint-disable-next-line
  const [submitSelectedProperties, { isLoading: isSubmitting }] =
    useSubmitSelectedPropertiesMutation();

  // eslint-disable-next-line
  const { data: ratePlansData, isFetching: isFetchingRatePlans } =
    useGetRatePlansQuery(
      { propertyId: currentPropertyIdForRatePlans! },
      {
        skip:
          !currentPropertyIdForRatePlans ||
          ratePlansFetched[currentPropertyIdForRatePlans],
      }
    );

  // Update the ref when we start fetching for a new property
  useEffect(() => {
    if (
      currentPropertyIdForRatePlans &&
      !ratePlansFetched[currentPropertyIdForRatePlans]
    ) {
      processingPropertyIdRef.current = currentPropertyIdForRatePlans;
    }
  }, [currentPropertyIdForRatePlans, ratePlansFetched]);

  // Update properties with fetched rate plans
  useEffect(() => {
    // Now ratePlansData is directly the API response: { success, data, message }
    const ratePlansArray = ratePlansData?.data;

    if (
      ratePlansData?.success &&
      Array.isArray(ratePlansArray) &&
      !isFetchingRatePlans &&
      currentPropertyIdForRatePlans
    ) {
      // Use the currentPropertyIdForRatePlans as the key since we know which property we requested
      const propertyIdKey = currentPropertyIdForRatePlans;

      // Only process if we haven't already processed this property
      if (!ratePlansFetched[propertyIdKey]) {
        // If there are rate plans, verify they belong to this property
        if (ratePlansArray.length > 0) {
          const responsePropertyId = ratePlansArray[0]?.propertyId;
          // Only update if the response matches the property we requested
          if (String(responsePropertyId) === propertyIdKey) {
            setPropertiesWithRatePlans((prev) =>
              prev.map((prop) =>
                String(prop.id) === propertyIdKey
                  ? {
                      ...prop,
                      ratePlans: ratePlansArray.map((rp: RatePlanDto) => ({
                        id: rp.id,
                        name: rp.name,
                        code: rp.code,
                        shortDescription: rp.shortDescription || '',
                        policyName: rp.policyName,
                        isRefundable: rp.isRefundable,
                        currencyCode: rp.currencyCode,
                      })),
                    }
                  : prop
              )
            );

            // Mark this property as fetched
            setRatePlansFetched((prev) => ({
              ...prev,
              [propertyIdKey]: true,
            }));
            setCurrentPropertyIdForRatePlans(null);
          }
        } else {
          // Empty rate plans array - mark property as having no rate plans
          setPropertiesWithRatePlans((prev) =>
            prev.map((prop) =>
              String(prop.id) === propertyIdKey
                ? { ...prop, ratePlans: [] }
                : prop
            )
          );

          // Mark this property as fetched
          setRatePlansFetched((prev) => ({
            ...prev,
            [propertyIdKey]: true,
          }));
          setCurrentPropertyIdForRatePlans(null);
        }
      }
    }
  }, [
    ratePlansData,
    isFetchingRatePlans,
    ratePlansFetched,
    currentPropertyIdForRatePlans,
  ]);

  // Initialize properties when properties are loaded
  useEffect(() => {
    if (propertiesResponse?.data?.length) {
      setPropertiesWithRatePlans([...propertiesResponse.data]);
    }
  }, [propertiesResponse]);

  const handlePropertySelection = (selected: Record<string, number[]>) => {
    const propertyIds = Object.keys(selected);
    setSelectedPropertyIds(propertyIds);
  };

  const handlePropertyExpand = (propertyId: string) => {
    // Fetch rate plans when property is expanded (if not already fetched)
    if (
      !ratePlansFetched[propertyId] &&
      !propertiesWithRatePlans.find((p) => String(p.id) === propertyId)
        ?.ratePlans
    ) {
      setCurrentPropertyIdForRatePlans(propertyId);
    }
  };

  // Validation: Check if all selected properties have at least one rate plan
  const isValidSelection = (selections: Record<string, number[]>): boolean => {
    // If no properties selected, invalid
    if (Object.keys(selections).length === 0) {
      return false;
    }

    // Check if every selected property has at least one rate plan
    return Object.entries(selections).every(([_propertyId, ratePlanIds]) => {
      return Array.isArray(ratePlanIds) && ratePlanIds.length > 0;
    });
  };

  if (isLoadingProperties) return <Loader />;
  if (isPropertiesError)
    return <div>Error loading properties. Please try again later.</div>;

  return (
    <div className="property-selection">
      <img className="bg-left" src={LeftBg} alt="" />
      <img className="bg-right" src={RightBg} alt="" />

      <header className="header">
        <div className="header__inner">
          <div className="header__brand">
            <img src={HTLogo} alt="Hotel Trader" />
          </div>
        </div>
      </header>

      <div className="property-selection__main">
        <div className="property-selection__main__inner">
          <h2 className="title">Hi, there!</h2>
          <p className="subtitle">
            Choose the properties and their rate plans that you want to disconnect from HotelTrader.
          </p>

          <Formik
            initialValues={{ selections: {} }}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                setSubmitError(null);

                // Convert selections to the expected backend format
                // values.selections = { propertyId: [ratePlanId1, ratePlanId2, ...] }
                const selectedProperties = Object.entries(values.selections)
                  .map(([propertyId, ratePlanIds]) => {
                    // Find the property to get its details
                    const property = propertiesWithRatePlans.find(
                      (p) => String(p.id) === propertyId
                    );

                    // Type guard: ratePlanIds should be number[]
                    const ratePlanIdArray = Array.isArray(ratePlanIds)
                      ? ratePlanIds
                      : [];

                    // Get full rate plan objects for the selected IDs
                    const ratePlans = (property?.ratePlans || [])
                      .filter((rp) => ratePlanIdArray.includes(rp.id))
                      .map((rp) => ({
                        id: rp.id,
                        code: rp.code,
                        name: rp.name,
                      }));

                    return {
                      id: property?.id || Number(propertyId),
                      name: property?.name || '',
                      code: property?.code || propertyId,
                      ratePlans: ratePlans,
                    };
                  })
                  .filter((prop) => prop.ratePlans.length > 0); // Only include properties with selected rate plans

                // Validate that at least one property with rate plans is selected
                if (selectedProperties.length === 0) {
                  setSubmitError(
                    'Please select at least one rate plan for the selected properties'
                  );
                  setSubmitting(false);
                  return;
                }

                const response = await submitSelectedProperties(
                  selectedProperties
                ).unwrap();

                if (response.success) {
                  setShowModal(true);
                } else {
                  setSubmitError(
                    response.message || 'Failed to submit selection'
                  );
                }
              } catch (error: any) {
                console.error('Error submitting selection:', error);
                setSubmitError(
                  error.data?.message ||
                    'An error occurred while submitting your selection'
                );
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ values, setFieldValue, isSubmitting: isFormSubmitting }) => (
              <Form>
                <PropertyCheckList
                  data={{ properties: propertiesWithRatePlans }}
                  value={values.selections}
                  onChange={(next) => {
                    setFieldValue('selections', next);
                    handlePropertySelection(next);
                  }}
                  showRatePlans={true}
                  onPropertyExpand={handlePropertyExpand}
                />
                <div className="footer">
                  <div className="confirm-bar">
                    <button
                      type="submit"
                      className="confirm-btn"
                      disabled={
                        isFormSubmitting || !isValidSelection(values.selections)
                      }
                    >
                      {isFormSubmitting ? 'Disconnecting...' : 'Disconnect Selected'}
                    </button>
                  </div>
                </div>
                {submitError && (
                  <div className="error-message">{submitError}</div>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        closeButton={false}
        size="small"
        body={
          <div className="success-modal">
            <img src={CheckTick} alt="Success" className="success-icon" />
            <h3>Success!</h3>
            <p>
              Your selected properties and rate plans have been successfully disconnected.
            </p>
            <hr />
            <button className="confirm-btn" onClick={() => setShowModal(false)}>
              Close
            </button>
          </div>
        }
      />
    </div>
  );
};

export default PropertySelection;
